import request from "../../utils/request";
import { Result } from "../../models/general";
import { EquipmentPriority } from "./businessRoles";

export enum AssetStatus {
  Available = 'Available',
  Issued = 'Issued',
  UnderMaintenance = 'UnderMaintenance',
  Retired = 'Retired',
}

export enum AssetCondition {
  Excellent = 'Excellent',
  Good = 'Good',
  Fair = 'Fair',
  Poor = 'Poor',
}

export interface AssetView {
  id: number;
  name: string;
  categoryId: number;
  categoryName?: string;
  /** Level-2 type of `categoryName`, which is a level-3 SKU. */
  categoryParentName?: string;
  serialNumber?: string;
  purchasePrice?: number;
  purchaseDate?: string;
  warrantyExpiry?: string;
  condition: AssetCondition;
  status: AssetStatus;
  location?: string;
  storageLocationId?: number;
  storageLocationName?: string;
  notes?: string;
  assignedToUserId?: string;
  assignedToUserName?: string;
  createdOn: string;
}

export interface AssetLocationView {
  id: number;
  name: string;
  address?: string;
  notes?: string;
  assetCount: number;
}

export interface CreateAssetLocationInput {
  name: string;
  address?: string;
  notes?: string;
}

export interface UpdateAssetLocationInput extends CreateAssetLocationInput {
  id: number;
}

export interface AssetReturnReasonView {
  id: number;
  reason: string;
}

export interface CreateAssetReturnReasonInput {
  reason: string;
}

export interface UpdateAssetReturnReasonInput extends CreateAssetReturnReasonInput {
  id: number;
}

export interface ReplenishmentRowView {
  categoryId: number;
  skuName: string;
  l2Name?: string;
  available: number;
  totalOwned: number;
  threshold?: number;
  thresholdType?: ReplenishThresholdType;
  replenishBaseline?: number;
}

export interface UpdateReplenishmentThresholdInput {
  categoryId: number;
  threshold?: number;
  thresholdType?: ReplenishThresholdType;
}

export enum ReplenishThresholdType {
  Amount = 'Amount',
  Percentage = 'Percentage',
}

export interface AssetCategoryView {
  id: number;
  name: string;
  description?: string;
  assetCount: number;
  level?: number;
  parentId?: number;
  /** The parent's name, resolved server-side. A level-3 SKU alone does not say what kind of thing it is. */
  parentName?: string;
  availableCount: number;
  totalOwnedCount: number;
  replenishThreshold?: number;
  replenishThresholdType?: ReplenishThresholdType;
}

export interface AssetListView {
  items: AssetView[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AssetStatsView {
  totalAssets: number;
  available: number;
  issued: number;
  underMaintenance: number;
  retired: number;
  totalValue: number;
  awaitingFulfillment: number;
}

export interface EquipmentMatchedAssetView {
  assetName: string;
  condition: AssetCondition;
  weight: number;
}

export interface EquipmentCategoryCoverageView {
  categoryId: number;            // L2 category the need is declared at
  neededItem: string;
  isCovered: boolean;
  priority: EquipmentPriority;   // weight input: Essential 4..Optional 1
  quantity: number;              // needed units of this category
  issuedCount: number;           // matching active assets held (fill = min(issuedCount/quantity, 1))
  conditionPercent?: number;
  matchedAssets: EquipmentMatchedAssetView[];
  /** Listed, but crossed off by an asset manager so it no longer moves the score. */
  isWaived?: boolean;
  waivedByName?: string | null;
  waivedOn?: string | null;
  waiverNote?: string | null;
  isAddedByAssetManager?: boolean;
  /** Provenance for a hand-added requirement. The waived* trio is null while the requirement counts. */
  addedByName?: string | null;
  addedOn?: string | null;
  /** Set when a waiver lifted because a new role or goal started requiring this. Names that source. */
  waiverLiftedBySource?: string | null;
}

export interface AddedRequirementView {
  needs: EquipmentCategoryCoverageView[];
}

export interface RoleRequirementView {
  /** Absent on a payload from a backend older than the deep-link change; the name then renders plain. */
  roleId?: number;
  roleName: string;
  needs: EquipmentCategoryCoverageView[];
}

export interface GoalRequirementView {
  /** Goal keys are strings, unlike a business role's numeric id. */
  goalId?: string;
  goalTitle: string;
  needs: EquipmentCategoryCoverageView[];
}

// .NET enums serialize as the member NAME by default in this project (see AssetStatus/AssetCondition above).
export enum EquipmentIssueSource {
  Role = 'Role',
  Request = 'Request',
  Standalone = 'Standalone',
  Goal = 'Goal',
}

export interface EquipmentIssuedAssetView {
  assetId: number;
  assetName: string;
  categoryName: string;
  source: EquipmentIssueSource;
  sourceDetail?: string;
  conditionPercent: number;
  returnDue?: boolean;
  returnDueGoalTitle?: string;
}

export interface EquipmentCoverageRowView {
  employeeId: string;
  employeeName?: string;
  department?: string;
  roleName?: string;
  coveragePercent: number;
  conditionPercent?: number;
  items: EquipmentCategoryCoverageView[];
  roleRequirements?: RoleRequirementView[];
  goalRequirements?: GoalRequirementView[];
  addedRequirements?: AddedRequirementView;
  issuedAssets?: EquipmentIssuedAssetView[];
  /** Every requirement is crossed off, so there is nothing left to measure. Scoring must skip the row. */
  isUnmeasured?: boolean;
}

export interface OtherHolderView {
  employeeId: string;
  employeeName?: string;
  department?: string;
  issuedAssets: EquipmentIssuedAssetView[]; // source is Request or Standalone only
}

export interface UnresolvedRequirementView {
  categoryId: number;
  /** The category's name if it still exists at any level; absent once it is gone entirely. */
  name?: string;
  /** Its level, when it exists. A requirement must sit at level 2 to be matchable. */
  level?: number;
}

export interface EquipmentCoverageView {
  rows: EquipmentCoverageRowView[];
  underCoveredCount: number;
  /** Requirements excluded from the score company-wide, shown beside underCoveredCount. */
  waivedCount: number;
  totalWithRequirements: number;
  otherHolders?: OtherHolderView[];
  /** Requirements pointing at a category that is not a level-2 type, so nothing can satisfy them. */
  unresolvedRequirements?: UnresolvedRequirementView[];
  /** Employees left out of `rows` because every requirement they hold is unresolvable. */
  employeesExcludedByUnresolvedRequirements?: number;
}

export interface AssetListQuery {
  search?: string;
  categoryL1Id?: number;
  categoryL2Id?: number;
  categoryL3Id?: number;
  storageLocationId?: number;
  status?: AssetStatus;
  condition?: AssetCondition;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

export interface CreateAssetInput {
  categoryId: number;
  serialNumber?: string;
  serialNumbers?: string[];
  purchasePrice?: number;
  purchaseDate?: string;
  warrantyExpiry?: string;
  condition: AssetCondition;
  location?: string;
  storageLocationId?: number;
  quantity?: number;
  notes?: string;
}

export interface UpdateAssetInput extends CreateAssetInput {
  id: number;
}

export interface CreateAssetCategoryInput {
  name: string;
  description?: string;
  level: number;
  parentId?: number;
}

export interface UpdateAssetCategoryInput extends CreateAssetCategoryInput {
  id: number;
  replenishThreshold?: number;
  replenishThresholdType?: ReplenishThresholdType;
}

// Assets
export async function getAssets(query: AssetListQuery): Promise<Result<AssetListView>> {
  const params = new URLSearchParams();
  if (query.search) params.append("search", query.search);
  if (query.categoryL1Id !== undefined) params.append("categoryL1Id", String(query.categoryL1Id));
  if (query.categoryL2Id !== undefined) params.append("categoryL2Id", String(query.categoryL2Id));
  if (query.categoryL3Id !== undefined) params.append("categoryL3Id", String(query.categoryL3Id));
  if (query.storageLocationId !== undefined) params.append("storageLocationId", String(query.storageLocationId));
  if (query.status !== undefined) params.append("status", String(query.status));
  if (query.condition !== undefined) params.append("condition", String(query.condition));
  if (query.sortBy) params.append("sortBy", query.sortBy);
  if (query.sortDescending) params.append("sortDescending", "true");
  if (query.page) params.append("page", String(query.page));
  if (query.pageSize) params.append("pageSize", String(query.pageSize));

  const response = await request.get<Result<AssetListView>>(`Asset/GetAssets?${params.toString()}`);
  return response.data;
}

export async function getAsset(id: number): Promise<Result<AssetView>> {
  const response = await request.get<Result<AssetView>>(`Asset/GetAsset/${id}`);
  return response.data;
}

export async function createAsset(input: CreateAssetInput): Promise<Result<AssetView>> {
  const response = await request.post<Result<AssetView>>("Asset/CreateAsset", input);
  return response.data;
}

export async function updateAsset(input: UpdateAssetInput): Promise<Result<AssetView>> {
  const response = await request.put<Result<AssetView>>("Asset/UpdateAsset", input);
  return response.data;
}

export async function archiveAsset(id: number): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>(`Asset/ArchiveAsset/${id}`);
  return response.data;
}

export async function getAssetStats(): Promise<Result<AssetStatsView>> {
  const response = await request.get<Result<AssetStatsView>>("Asset/GetAssetStats");
  return response.data;
}

export interface EquipmentCoverageQuery {
  branchId?: number;
  departmentId?: number;
  businessRoleId?: number;
}

export async function getEquipmentCoverage(query?: EquipmentCoverageQuery): Promise<Result<EquipmentCoverageView>> {
  const params = new URLSearchParams();
  if (query?.branchId !== undefined) params.append("branchId", String(query.branchId));
  if (query?.departmentId !== undefined) params.append("departmentId", String(query.departmentId));
  if (query?.businessRoleId !== undefined) params.append("businessRoleId", String(query.businessRoleId));
  const qs = params.toString();
  const response = await request.get<Result<EquipmentCoverageView>>(`Asset/GetEquipmentCoverage${qs ? `?${qs}` : ""}`);
  return response.data;
}

export interface CoverageRecommendationItemView {
  priority: EquipmentPriority;
  category: string;
  action: string;
  detail: string;
}

// Distinguishes "nothing to analyse" from "no AI key"; both used to arrive as aiAvailable: false.
// STRING values, not 0/1/2: Program.cs registers a StringEnumConverter, so every enum reaches the
// SPA as its NAME. As numbers this compared "NoRequirements" === 1, which is never true, so the
// panel's settled-answer state never engaged and its button stayed clickable forever (spec D2).
export enum CoverageUnavailableReason {
  None = "None",
  NoRequirements = "NoRequirements",
  AiNotConfigured = "AiNotConfigured",
}

export interface CoverageRecommendationView {
  summary: string;
  recommendations: CoverageRecommendationItemView[];
  aiAvailable: boolean;
  reason: CoverageUnavailableReason;
}

// AI advisory recommendations for one employee's equipment coverage (mirrors AssetAiController).
export async function getCoverageRecommendations(
  employeeId: string
): Promise<Result<CoverageRecommendationView>> {
  const response = await request.post<Result<CoverageRecommendationView>>(
    "AssetAi/CoverageRecommendations",
    { employeeId }
  );
  return response.data;
}

// ─── Asset Overview recommendations ───

export interface AvailableSkuView {
  categoryId: number;
  skuName: string;
  available: number;
}

export interface AssetRecommendationEmployeeView {
  employeeId: string;
  employeeName: string;
  departmentName?: string;
  reason: string;
  priority: EquipmentPriority;
  gap: number;
  isTransferred: boolean;
  /** The L3 SKU the AI picked for this person, under the row's L2 category. */
  suggestedCategoryId?: number | null;
  suggestedSkuName?: string | null;
  skuRationale?: string | null;
  isWaived: boolean;
  waivedByName?: string | null;
  waivedOn?: string | null;
  /** Always null here: the lifted state is shown on the Coverage tab, not in the drawer. */
  waiverLiftedBySource?: string | null;
  isAddedByAssetManager: boolean;
  /** Effective requirement, override applied, plus whether an override is what set it. */
  quantity?: number;
  isPriorityOverridden?: boolean;
  isQuantityOverridden?: boolean;
  countsTowardCoverage: boolean;
}

export interface AssetRecommendationRowView {
  categoryId: number;            // L2 category the row represents
  categoryName: string;
  available: number;             // summed across every child SKU in stock
  priority: EquipmentPriority;
  isPriorityOverridden: boolean;
  isSkipped: boolean;
  generatedOn: string;
  skus: AvailableSkuView[];
  employees: AssetRecommendationEmployeeView[];
}

export interface AssetRecommendationListView {
  rows: AssetRecommendationRowView[];
  hasSnapshot: boolean;
  generatedOn?: string;
  hiddenCount: number;
}

export interface OverrideRecommendationPriorityInput {
  categoryId: number;
  priority?: EquipmentPriority;
}

export interface TransferTargetInput {
  employeeId: string;
  /** The L3 SKU this person actually gets. FulfillAssetRequest matches on it exactly. */
  skuCategoryId: number;
}

export interface CreateAssetRequestsForEmployeesInput {
  /** The L2 row the recommendation came from. Used to find the snapshot, never to create a request. */
  categoryId: number;
  employees: TransferTargetInput[];
}

export interface SetEquipmentRequirementOverrideInput {
  employeeId: string;
  categoryId: number;
  countsTowardCoverage: boolean;
  isAdded: boolean;
  priority?: EquipmentPriority;
  quantity?: number;
  note?: string;
}

export async function getAssetRecommendations(includeHidden = false): Promise<Result<AssetRecommendationListView>> {
  const response = await request.get<Result<AssetRecommendationListView>>(
    `Asset/GetAssetRecommendations?includeHidden=${includeHidden}`
  );
  return response.data;
}

export async function generateAssetRecommendations(): Promise<Result<number>> {
  const response = await request.post<Result<number>>("Asset/GenerateAssetRecommendations");
  return response.data;
}

export async function skipAssetRecommendation(categoryId: number): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>("Asset/SkipAssetRecommendation", { categoryId });
  return response.data;
}

export async function unskipAssetRecommendation(categoryId: number): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>("Asset/UnskipAssetRecommendation", { categoryId });
  return response.data;
}

export async function overrideRecommendationPriority(input: OverrideRecommendationPriorityInput): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>("Asset/OverrideRecommendationPriority", input);
  return response.data;
}

export async function createAssetRequestsForEmployees(input: CreateAssetRequestsForEmployeesInput): Promise<Result<number>> {
  const response = await request.post<Result<number>>("Asset/CreateAssetRequestsForEmployees", input);
  return response.data;
}

export async function setEquipmentRequirementOverride(input: SetEquipmentRequirementOverrideInput): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>("EquipmentRequirement/SetEquipmentRequirementOverride", input);
  return response.data;
}

export async function clearEquipmentRequirementOverride(employeeId: string, categoryId: number): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>("EquipmentRequirement/ClearEquipmentRequirementOverride", { employeeId, categoryId });
  return response.data;
}

// Bulk CSV Import
export interface CsvRowValidation {
  row: number;
  field: string;
  message: string;
  severity: "error" | "warning";
}

export interface CsvValidationResultView {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  errors: CsvRowValidation[];
  warnings: CsvRowValidation[];
  // Names the file uses that the company has not set up yet. The import screen offers to create
  // these in place rather than sending the user to Settings and back.
  missingCategories: string[];
  missingLocations: string[];
}

export interface BulkImportResultView {
  importedCount: number;
  skippedCount: number;
  errors: CsvRowValidation[];
}

export async function validateCsvImport(file: File): Promise<Result<CsvValidationResultView>> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await request.post<Result<CsvValidationResultView>>(
    "Asset/ValidateCsvImport",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
}

export async function importCsvAssets(file: File): Promise<Result<BulkImportResultView>> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await request.post<Result<BulkImportResultView>>(
    "Asset/ImportCsvAssets",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
}

// Issuance & Return
export enum AssetIssuanceStatus {
  Active = 'Active',
  Returned = 'Returned',
}

export interface AssetIssuanceView {
  id: number;
  assetId: number;
  assetName?: string;
  assetRequestId?: number;
  issuedToUserId: string;
  issuedToUserName?: string;
  issueDate: string;
  expectedReturnDate?: string;
  purpose: string;
  conditionAtIssue: AssetCondition;
  issueNotes?: string;
  status: AssetIssuanceStatus;
  returnDate?: string;
  conditionAtReturn?: AssetCondition;
  returnReasonId?: number;
  returnReasonName?: string;
  hasDamage: boolean;
  damageDescription?: string;
  damagePhotoUrls: string[];
  repairRequired: boolean;
  repairCost?: number;
  receivedBy?: string;
  digitalSignature?: string;
  returnNotes?: string;
  createdOn: string;
}

export interface IssueAssetInput {
  assetId: number;
  issuedToUserId: string;
  issueDate: string;
  expectedReturnDate?: string;
  purpose: string;
  condition: AssetCondition;
  notes?: string;
  otp: string;
}

export interface ReturnAssetInput {
  assetId: number;
  returnDate: string;
  condition: AssetCondition;
  returnReasonId: number;
  hasDamage: boolean;
  damageDescription?: string;
  repairRequired: boolean;
  repairCost?: number;
  digitalSignature: string;
  notes?: string;
}

export interface EmployeeSimpleView {
  id: string;
  fullName: string;
  department?: string;
}

export interface AssetOtpView {
  assetId: number;
  sent: boolean;
}

export async function generateOtp(assetId: number, employeeId: string): Promise<Result<AssetOtpView>> {
  const response = await request.post<Result<AssetOtpView>>(
    `Asset/GenerateOtp?assetId=${assetId}&employeeId=${encodeURIComponent(employeeId)}`
  );
  return response.data;
}

export async function issueAsset(input: IssueAssetInput): Promise<Result<AssetIssuanceView>> {
  const response = await request.post<Result<AssetIssuanceView>>("Asset/IssueAsset", input);
  return response.data;
}

export async function returnAsset(input: ReturnAssetInput): Promise<Result<AssetIssuanceView>> {
  const response = await request.post<Result<AssetIssuanceView>>("Asset/ReturnAsset", input);
  return response.data;
}

export async function getAssetHistory(assetId: number): Promise<Result<AssetIssuanceView[]>> {
  const response = await request.get<Result<AssetIssuanceView[]>>(`Asset/GetAssetHistory/${assetId}`);
  return response.data;
}

export async function searchEmployees(search?: string): Promise<Result<EmployeeSimpleView[]>> {
  const params = search ? `?search=${encodeURIComponent(search)}` : "";
  const response = await request.get<Result<EmployeeSimpleView[]>>(`Asset/SearchEmployees${params}`);
  return response.data;
}

// Maintenance
export enum MaintenanceType {
  Cleaning = 'Cleaning',
  Repair = 'Repair',
  Replacement = 'Replacement',
  Inspection = 'Inspection',
  Upgrade = 'Upgrade',
  Calibration = 'Calibration',
  PreventiveMaintenance = 'PreventiveMaintenance',
  Other = 'Other',
}

export interface AssetMaintenanceView {
  id: number;
  assetId: number;
  type: MaintenanceType;
  maintenanceDate: string;
  cost: number;
  vendor?: string;
  performedBy?: string;
  notes?: string;
  createdOn: string;
}

export interface CreateMaintenanceInput {
  assetId: number;
  type: MaintenanceType;
  maintenanceDate: string;
  cost: number;
  vendor?: string;
  performedBy?: string;
  notes?: string;
}

export interface DepreciationYearView {
  year: number;
  yearNumber: number;
  originalValue: number;
  depreciationExpense: number;
  accumulatedDepreciation: number;
  bookValue: number;
  isCurrent: boolean;
}

export interface DepreciationScheduleView {
  originalValue: number;
  currentBookValue: number;
  totalDepreciation: number;
  usefulLifeYears: number;
  currency?: string;
  schedule: DepreciationYearView[];
}

export async function getMaintenanceHistory(assetId: number): Promise<Result<AssetMaintenanceView[]>> {
  const response = await request.get<Result<AssetMaintenanceView[]>>(`Asset/GetMaintenanceHistory/${assetId}`);
  return response.data;
}

export async function createMaintenance(input: CreateMaintenanceInput): Promise<Result<AssetMaintenanceView>> {
  const response = await request.post<Result<AssetMaintenanceView>>("Asset/CreateMaintenance", input);
  return response.data;
}

export async function getDepreciationSchedule(assetId: number, usefulLifeYears: number = 5): Promise<Result<DepreciationScheduleView>> {
  const params = new URLSearchParams();
  params.append("assetId", String(assetId));
  params.append("usefulLifeYears", String(usefulLifeYears));
  const response = await request.get<Result<DepreciationScheduleView>>(`Asset/GetDepreciationSchedule?${params.toString()}`);
  return response.data;
}

// Categories
export async function getCategories(): Promise<Result<AssetCategoryView[]>> {
  const response = await request.get<Result<AssetCategoryView[]>>("Asset/GetCategories");
  return response.data;
}

export async function createCategory(input: CreateAssetCategoryInput): Promise<Result<AssetCategoryView>> {
  const response = await request.post<Result<AssetCategoryView>>("Asset/CreateCategory", input);
  return response.data;
}

export async function updateCategory(input: UpdateAssetCategoryInput): Promise<Result<AssetCategoryView>> {
  const response = await request.put<Result<AssetCategoryView>>("Asset/UpdateCategory", input);
  return response.data;
}

export async function archiveCategory(id: number): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>(`Asset/ArchiveCategory/${id}`);
  return response.data;
}

export async function getCategoryIdsByLocation(locationId: number): Promise<Result<number[]>> {
  const response = await request.get<Result<number[]>>(`Asset/GetCategoryIdsByLocation/${locationId}`);
  return response.data;
}

// Asset Locations
export async function getAssetLocations(): Promise<Result<AssetLocationView[]>> {
  const response = await request.get<Result<AssetLocationView[]>>("Asset/GetAssetLocations");
  return response.data;
}

export async function createAssetLocation(input: CreateAssetLocationInput): Promise<Result<AssetLocationView>> {
  const response = await request.post<Result<AssetLocationView>>("Asset/CreateAssetLocation", input);
  return response.data;
}

export async function updateAssetLocation(input: UpdateAssetLocationInput): Promise<Result<AssetLocationView>> {
  const response = await request.put<Result<AssetLocationView>>("Asset/UpdateAssetLocation", input);
  return response.data;
}

export async function archiveAssetLocation(id: number): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>(`Asset/ArchiveAssetLocation/${id}`);
  return response.data;
}

// Return Reasons
export async function getReturnReasons(): Promise<Result<AssetReturnReasonView[]>> {
  const response = await request.get<Result<AssetReturnReasonView[]>>("Asset/GetReturnReasons");
  return response.data;
}

export async function createReturnReason(input: CreateAssetReturnReasonInput): Promise<Result<AssetReturnReasonView>> {
  const response = await request.post<Result<AssetReturnReasonView>>("Asset/CreateReturnReason", input);
  return response.data;
}

export async function updateReturnReason(input: UpdateAssetReturnReasonInput): Promise<Result<AssetReturnReasonView>> {
  const response = await request.put<Result<AssetReturnReasonView>>("Asset/UpdateReturnReason", input);
  return response.data;
}

export async function archiveReturnReason(id: number): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>(`Asset/ArchiveReturnReason/${id}`);
  return response.data;
}

// Replenishment
export async function getReplenishmentSettings(): Promise<Result<ReplenishmentRowView[]>> {
  const response = await request.get<Result<ReplenishmentRowView[]>>("Asset/GetReplenishmentSettings");
  return response.data;
}

export async function updateReplenishmentThreshold(input: UpdateReplenishmentThresholdInput): Promise<Result<ReplenishmentRowView>> {
  const response = await request.post<Result<ReplenishmentRowView>>("Asset/UpdateReplenishmentThreshold", input);
  return response.data;
}

// ─── Asset Requests ───

export enum AssetRequestStatus {
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected',
  Fulfilled = 'Fulfilled',
}

export enum AssetRequestPriority {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
}

export interface AssetRequestView {
  id: number;
  categoryId: number;
  categoryName?: string;
  employeeId: string;
  employeeName?: string;
  department?: string;
  reason: string;
  priority: AssetRequestPriority;
  status: AssetRequestStatus;
  reviewedById?: string;
  reviewedByName?: string;
  reviewComment?: string;
  reviewedDate?: string;
  fulfilledByIssuanceId?: number;
  createdOn: string;
  /**
   * CO-814 Q1 for assets (review-3 finding 6): only on the requester's OWN Pending rows. "Chief" = nobody
   * else can review it, so the company's Chief decides; "Unroutable" = no other asset reviewer and no Chief.
   */
  pendingWith?: 'AssetReviewer' | 'Chief' | 'Unroutable' | null;
}

export interface AssetRequestListView {
  items: AssetRequestView[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CreateAssetRequestInput {
  categoryId: number;
  reason: string;
  priority: AssetRequestPriority;
}

export interface ReviewAssetRequestInput {
  requestId: number;
  status: AssetRequestStatus;
  comment?: string;
}

export interface FulfillAssetRequestInput {
  requestId: number;
  assetId: number;
  condition: AssetCondition;
  notes?: string;
  otp: string;
}

export interface AssetRequestListQuery {
  search?: string;
  status?: AssetRequestStatus;
  priority?: AssetRequestPriority;
  page?: number;
  pageSize?: number;
}

export interface MyAssetsView {
  current: MyAssetItem[];
  history: MyAssetItem[];
}

export interface MyAssetItem {
  assetId: number;
  assetName: string;
  categoryName?: string;
  serialNumber?: string;
  condition: AssetCondition;
  assetStatus: AssetStatus;
  issueDate: string;
  expectedReturnDate?: string;
  returnDate?: string;
  purpose?: string;
}

export async function createAssetRequest(input: CreateAssetRequestInput): Promise<Result<AssetRequestView>> {
  const response = await request.post<Result<AssetRequestView>>("Asset/CreateAssetRequest", input);
  return response.data;
}

export async function getMyAssetRequests(): Promise<Result<AssetRequestView[]>> {
  const response = await request.get<Result<AssetRequestView[]>>("Asset/GetMyAssetRequests");
  return response.data;
}

export async function getAssetRequests(query: AssetRequestListQuery): Promise<Result<AssetRequestListView>> {
  const params = new URLSearchParams();
  if (query.search) params.append("search", query.search);
  if (query.status !== undefined) params.append("status", String(query.status));
  if (query.priority !== undefined) params.append("priority", String(query.priority));
  if (query.page) params.append("page", String(query.page));
  if (query.pageSize) params.append("pageSize", String(query.pageSize));

  const response = await request.get<Result<AssetRequestListView>>(`Asset/GetAssetRequests?${params.toString()}`);
  return response.data;
}

export async function reviewAssetRequest(input: ReviewAssetRequestInput): Promise<Result<AssetRequestView>> {
  const response = await request.post<Result<AssetRequestView>>("Asset/ReviewAssetRequest", input);
  return response.data;
}

export async function getMyAssets(): Promise<Result<MyAssetsView>> {
  const response = await request.get<Result<MyAssetsView>>("Asset/GetMyAssets");
  return response.data;
}

export async function getEmployeeAssets(employeeId: string): Promise<Result<MyAssetsView>> {
  const response = await request.get<Result<MyAssetsView>>(`Asset/GetEmployeeAssets/${employeeId}`);
  return response.data;
}

export async function fulfillAssetRequest(input: FulfillAssetRequestInput): Promise<Result<AssetIssuanceView>> {
  const response = await request.post<Result<AssetIssuanceView>>('Asset/FulfillAssetRequest', input);
  return response.data;
}

export async function getUnfulfilledRequestCount(): Promise<Result<number>> {
  const response = await request.get<Result<number>>('Asset/GetUnfulfilledRequestCount');
  return response.data;
}

// ─── Asset Settings ───

export interface AssetSettingsView {
  requireAssetIssuanceOtp: boolean;
}

export async function getAssetSettings(): Promise<Result<AssetSettingsView>> {
  const response = await request.get<Result<AssetSettingsView>>("Asset/GetAssetSettings");
  return response.data;
}

export async function updateAssetSettings(input: AssetSettingsView): Promise<Result<AssetSettingsView>> {
  const response = await request.post<Result<AssetSettingsView>>("Asset/UpdateAssetSettings", input);
  return response.data;
}

export async function getCategoriesForRequest(): Promise<Result<AssetCategoryView[]>> {
  const response = await request.get<Result<AssetCategoryView[]>>("Asset/GetAssetCategoriesForRequest");
  return response.data;
}

// ─── Asset Manager Role (Admin-only) ───

export interface AssetRoleMemberView {
  userId: string;
  fullName: string;
  department?: string;
  isAssetManager: boolean;
  /** True when the member is Hr: they have asset access WITHOUT holding the AssetManagement tag. */
  isImplicitAssetManager?: boolean;
}

export async function getAssetRoleMembers(search?: string): Promise<Result<AssetRoleMemberView[]>> {
  const params = search ? `?search=${encodeURIComponent(search)}` : "";
  const response = await request.get<Result<AssetRoleMemberView[]>>(`Asset/GetAssetRoleMembers${params}`);
  return response.data;
}

export async function grantAssetRole(userId: string): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>("Asset/GrantAssetRole", { userId });
  return response.data;
}

export async function revokeAssetRole(userId: string): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>("Asset/RevokeAssetRole", { userId });
  return response.data;
}

// Returns the raw file, not a Result envelope: the endpoint streams the workbook directly.
export async function downloadImportTemplate(): Promise<Blob> {
  const response = await request.get<Blob>("Asset/ImportTemplate", { responseType: "blob" });
  return response.data;
}
